class SearchAutocomplete extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.setupState()

    this.productIndex = 0
    this.controller = false
    this.noResultsTimeout = false
  }

  setupListeners () {
    super.setupListeners()

    document.addEventListener(
      'algolia.hooks.initialize',
      () => {
        this.registerAutocompleteOptions()
        this.registerMainTemplate()
        this.registerProductTemplate()
        this.registerNoResultsTemplate()
      },
      { once: true },
    )

    this.listeners.add(document, 'drawer-changed', this.focusInput.bind(this))
  }

  focusInput () {
    if (!this.closest('dialog')?.hasAttribute('open')) return

    this.querySelector('.aa-Input')?.focus()
  }

  registerAutocompleteOptions () {
    /* eslint-disable-next-line no-undef */
    algoliaShopify.hooks.registerHook(
      'beforeAutocompleteOptions',
      (options) => {
        options.autoFocus = true
        options.openOnFocus = true
        options.panelContainer = this.els.results.element
        options.detachedMediaQuery = 'none'

        return options
      },
    )
  }

  registerMainTemplate () {
    /* eslint-disable-next-line no-undef */
    algoliaShopify.hooks.registerHook(
      'beforeAutocompleteMainTemplate',
      (_defaultTemplate) => {
        this.els.noResultsContainer.element.setAttribute('hidden', '')
        this.els.resultsContainer.element.removeAttribute('hidden')
        clearTimeout(this.noResultsTimeout)

        /**
         * Reset product index on update so the product card slots can be
         * found again.
         */
        this.productIndex = 0

        /**
         * Abort in-flight queries as search input isn't debounced.
         */
        if (this.controller) {
          this.controller.abort()
        }

        this.controller = new AbortController()
        return _defaultTemplate
      },
    )
  }

  registerProductTemplate () {
    /* eslint-disable-next-line no-undef */
    algoliaShopify.hooks.registerHook(
      'beforeAutocompleteProductTemplate',
      (
        defaultTemplate,
        templateOptions,
        _distinct,
        _itemLink,
        trackSearchAttribution,
      ) => {
        const { html, item } = templateOptions

        if (!item.handle) {
          return defaultTemplate
        }

        /**
         * Algolia doesn't re-render placeholder on search update so need to use
         * index to find product card slot.
         */
        const placeholderKey = ['algolia-product-card', this.productIndex].join(
          '-',
        )

        this.renderProductCard(item.handle, placeholderKey)
        this.productIndex += 1

        return html`
          <div
            class="aa-ItemWrapper"
            onClick="${() => trackSearchAttribution(item)}"
          >
            <div
              class="aa-ItemContent prd-Card-placeholder"
              data-algolia-product-card-placeholder
              data-placeholder-key="${placeholderKey}"
              data-product-handle
            ></div>
          </div>
        `
      },
    )
  }

  renderProductCard (productHandle, placeholderKey) {
    try {
      window.requestAnimationFrame(async () => {
        const placeholderEl = this.querySelector(
          `[data-placeholder-key="${placeholderKey}"]`,
        )

        /**
         * Before getting product card HTML check if product card slot is
         * already showing that handle.
         * Algolia triggers re-renders on hover and other interactions.
         */
        if (!placeholderEl) return
        if (placeholderEl.dataset.productHandle === productHandle) return

        const productCardHtml = await this.getProductCardHtml(productHandle)
        if (!productCardHtml) return

        /**
         * Leave the product card slot element in place so we can re-render when
         * new product results are returned.
         */
        placeholderEl.dataset.productHandle = productHandle
        placeholderEl.innerHTML = productCardHtml
      })
    } catch (error) {
      console.error('productCard:renderFailed', error)
    }
  }

  async getProductCardHtml (productHandle) {
    const cachedProductCard = this.productCardCache.get(productHandle)

    if (cachedProductCard) {
      return cachedProductCard
    }

    const productUrl = [
      window.Shopify.routes.root,
      'products/',
      encodeURIComponent(productHandle),
    ].join('')

    try {
      const sectionHtml = await window.BAO.sectionRenderer.fetchSingle(
        'product-card-no-swatches',
        productUrl,
        this.controller,
      )

      const sectionDocument =
        window.BAO.sectionRenderer.convertToDOM(sectionHtml)

      const articleElement = sectionDocument.querySelector('article')

      const productCardHtml = articleElement ? articleElement.outerHTML : ''

      if (productCardHtml) {
        this.productCardCache.set(productHandle, productCardHtml)
      }

      return productCardHtml
    } catch (error) {
      if (error.name === 'AbortError') return
      console.error('productCard:fetchFailed', error)
      return ''
    }
  }

  setupState () {
    this.productCardCache = new Map()
  }

  registerNoResultsTemplate () {
    /* eslint-disable-next-line no-undef */
    algoliaShopify.hooks.registerHook(
      'beforeAutocompleteNoResultsTemplate',
      (_defaultTemplate, templateOptions) => {
        const { query } = templateOptions.state

        if (query === '') return

        this.els.noResultsContainer.element.removeAttribute('hidden')
        this.els.resultsContainer.element.setAttribute('hidden', '')

        const titleEl = this.els.noResultsTitle.element
        const title = titleEl.dataset.text.replace('#query#', query)
        titleEl.textContent = title

        requestAnimationFrame(() => {
          this.els.carousel.elements.forEach((carouselEl) => {
            carouselEl.dispatchEvent(
              new CustomEvent('BAO::CSS_CAROUSEL::VISIBLE'),
            )
          })
        })

        return false
      },
    )
  }
}

if (!customElements.get('search-autocomplete')) {
  customElements.define('search-autocomplete', SearchAutocomplete)
}
